import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Wrench, RotateCcw, Settings, FileText, HardHat, Users,
  Phone, Mail, MapPin, Calendar, ChevronRight, Search, RefreshCw,
  Clock, CheckCircle, XCircle, AlertCircle, TrendingUp, Filter,
  PlusCircle, ExternalLink, Calculator, Truck
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const DEMANDE_TYPES = [
  { id: 'all', label: 'Toutes', icon: Filter },
  { id: 'estimation_etude_sol', label: '📊 Estimation Étude Sol', icon: Calculator, color: 'bg-teal-500' },
  { id: 'estimation_foreuse', label: '🔧 Estimation Foreuse', icon: Truck, color: 'bg-indigo-500' },
  { id: 'vente', label: 'Vente (Vendeur)', icon: Wrench, color: 'bg-blue-500' },
  { id: 'vente_acheteur', label: 'Vente (Acheteur)', icon: Wrench, color: 'bg-blue-300' },
  { id: 'location', label: 'Location (Loueur)', icon: RotateCcw, color: 'bg-purple-500' },
  { id: 'location_locataire', label: 'Location (Locataire)', icon: RotateCcw, color: 'bg-purple-300' },
  { id: 'visite_rachat', label: 'Visite Rachat', icon: HardHat, color: 'bg-orange-500' },
  { id: 'service', label: 'Service', icon: Settings, color: 'bg-green-500' },
  { id: 'etude', label: 'Études', icon: FileText, color: 'bg-cyan-500' },
  { id: 'travaux', label: 'Travaux', icon: HardHat, color: 'bg-amber-500' },
  { id: 'forage_eau', label: 'Forage eau', icon: Filter, color: 'bg-sky-500' },
  { id: 'recrutement_employeur', label: 'Recrutement (Employeur)', icon: Users, color: 'bg-rose-500' },
  { id: 'recrutement_candidat', label: 'Recrutement (Candidat)', icon: Users, color: 'bg-pink-500' },
];

const STATUS_CONFIG = {
  nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-800', icon: Clock },
  contacte: { label: 'Contacté', color: 'bg-yellow-100 text-yellow-800', icon: Phone },
  mandat_signe: { label: 'Mandat signé', color: 'bg-purple-100 text-purple-800', icon: FileText },
  en_cours: { label: 'En cours', color: 'bg-orange-100 text-orange-800', icon: RefreshCw },
  negociation: { label: 'Négociation', color: 'bg-indigo-100 text-indigo-800', icon: TrendingUp },
  conclu: { label: 'Conclu', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  annule: { label: 'Annulé', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function DemandesDashboard() {
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [demandes, setDemandes] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedType, selectedStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build query params
      let params = new URLSearchParams();
      if (selectedType !== 'all') params.append('type', selectedType);
      if (selectedStatus) params.append('status', selectedStatus);
      
      const [demandesRes, statsRes] = await Promise.all([
        axios.get(`${API}/demandes/admin/all?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/demandes/admin/stats/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setDemandes(demandesRes.data.demandes || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching demandes:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (demandeId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/demandes/admin/${demandeId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Statut mis à jour : ${STATUS_CONFIG[newStatus].label}`);
      fetchData();
      // Update selected demande if open
      if (selectedDemande && selectedDemande.id === demandeId) {
        setSelectedDemande({ ...selectedDemande, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const convertToAnnonce = async (demandeId) => {
    try {
      setConverting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/demandes/admin/${demandeId}/convert-to-annonce`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Annonce créée avec succès !');
      
      // Update the demande in state
      const updatedDemande = { ...selectedDemande, annonce_id: response.data.product_id, status: 'en_cours' };
      setSelectedDemande(updatedDemande);
      fetchData();
      
      // Ask if user wants to edit the product
      if (window.confirm('Voulez-vous modifier l\'annonce maintenant ?')) {
        navigate(`/admin/products?edit=${response.data.product_id}`);
      }
    } catch (error) {
      console.error('Error converting to annonce:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setConverting(false);
    }
  };

  const filteredDemandes = demandes.filter(d => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      d.nom?.toLowerCase().includes(search) ||
      d.telephone?.includes(search) ||
      d.email?.toLowerCase().includes(search) ||
      d.localisation?.toLowerCase().includes(search)
    );
  });

  const getTypeIcon = (type) => {
    const config = DEMANDE_TYPES.find(t => t.id === type);
    return config ? config.icon : AlertCircle;
  };

  const getTypeColor = (type) => {
    const config = DEMANDE_TYPES.find(t => t.id === type);
    return config?.color || 'bg-gray-500';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const DemandeCard = ({ demande }) => {
    const TypeIcon = getTypeIcon(demande.type);
    const StatusConfig = STATUS_CONFIG[demande.status] || STATUS_CONFIG.nouveau;
    const StatusIcon = StatusConfig.icon;

    return (
      <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer group" 
            onClick={() => { setSelectedDemande(demande); setShowDetailModal(true); }}>
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-10 h-10 ${getTypeColor(demande.type)} rounded-lg flex items-center justify-center`}>
                <TypeIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A2540] group-hover:text-[#635BFF] transition-colors">
                  {demande.nom}
                </h3>
                <p className="text-xs text-gray-500 capitalize">{demande.type?.replace('_', ' ')}</p>
              </div>
            </div>
            <Badge className={StatusConfig.color}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {StatusConfig.label}
            </Badge>
          </div>

          {/* Contact Info */}
          <div className="space-y-1 mb-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Phone className="w-4 h-4 text-gray-400" />
              <a href={`tel:${demande.telephone}`} className="hover:text-[#635BFF]" onClick={e => e.stopPropagation()}>
                {demande.telephone}
              </a>
            </div>
            {demande.email && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="w-4 h-4 text-gray-400" />
                <a href={`mailto:${demande.email}`} className="hover:text-[#635BFF] truncate max-w-[200px]" onClick={e => e.stopPropagation()}>
                  {demande.email}
                </a>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="truncate">{demande.localisation}</span>
            </div>
          </div>

          {/* Additional Info based on type */}
          {demande.type === 'vente' && demande.type_materiel && (
            <div className="bg-blue-50 rounded-lg p-2 mb-3">
              <p className="text-xs text-blue-600 font-medium">Matériel: {demande.type_materiel}</p>
              {demande.prix_souhaite && (
                <p className="text-sm text-blue-800 font-bold">{demande.prix_souhaite.toLocaleString()} € souhaité</p>
              )}
            </div>
          )}

          {demande.type === 'etude' && (
            <div className="bg-cyan-50 rounded-lg p-2 mb-3">
              <p className="text-xs text-cyan-600 font-medium">Mission: {demande.type_mission}</p>
              <p className="text-xs text-cyan-700">Projet: {demande.type_projet}</p>
            </div>
          )}

          {demande.type === 'service' && (
            <div className="bg-green-50 rounded-lg p-2 mb-3">
              <p className="text-xs text-green-600 font-medium">Service: {demande.type_service}</p>
              {demande.urgence && <Badge className="bg-red-500 text-white text-xs">URGENT</Badge>}
            </div>
          )}

          {demande.type === 'visite_rachat' && (
            <div className="bg-orange-50 rounded-lg p-2 mb-3">
              <p className="text-xs text-orange-600 font-medium">Matériel: {demande.type_materiel_label || demande.type_materiel}</p>
              <p className="text-xs text-orange-700">{demande.marque} {demande.modele} {demande.annee && `(${demande.annee})`}</p>
            </div>
          )}

          {demande.type === 'estimation_etude_sol' && (
            <div className="bg-teal-50 rounded-lg p-2 mb-3">
              <p className="text-xs text-teal-600 font-medium">Mission: {demande.mission_label || demande.mission}</p>
              {(demande.estimation_bas && demande.estimation_haut) && (
                <p className="text-sm text-teal-800 font-bold">{demande.estimation_bas?.toLocaleString()} – {demande.estimation_haut?.toLocaleString()} € HT</p>
              )}
            </div>
          )}

          {demande.type === 'estimation_foreuse' && (
            <div className="bg-indigo-50 rounded-lg p-2 mb-3">
              <p className="text-xs text-indigo-600 font-medium">{demande.marque} {demande.modele}</p>
              {demande.is_residuel ? (
                <p className="text-xs text-amber-600 font-medium">Machine fin de cycle</p>
              ) : (demande.estimation_bas && demande.estimation_haut) && (
                <p className="text-sm text-indigo-800 font-bold">{demande.estimation_bas?.toLocaleString()} – {demande.estimation_haut?.toLocaleString()} €</p>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t">
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Calendar className="w-3 h-3" />
              {formatDate(demande.created_at)}
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#635BFF] transition-colors" />
          </div>
        </CardContent>
      </Card>
    );
  };

  const DetailModal = () => {
    if (!selectedDemande) return null;
    
    const TypeIcon = getTypeIcon(selectedDemande.type);
    const StatusConfig = STATUS_CONFIG[selectedDemande.status] || STATUS_CONFIG.nouveau;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="sticky top-0 bg-white border-b p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 ${getTypeColor(selectedDemande.type)} rounded-xl flex items-center justify-center`}>
                  <TypeIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#0A2540]">{selectedDemande.nom}</h2>
                  <p className="text-sm text-gray-500 capitalize">{selectedDemande.type?.replace('_', ' ')}</p>
                </div>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status Update */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => updateStatus(selectedDemande.id, key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      selectedDemande.status === key 
                        ? config.color + ' ring-2 ring-offset-2 ring-gray-400'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#635BFF]" />
                  <a href={`tel:${selectedDemande.telephone}`} className="text-[#635BFF] hover:underline font-medium">
                    {selectedDemande.telephone}
                  </a>
                </div>
                {selectedDemande.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#635BFF]" />
                    <a href={`mailto:${selectedDemande.email}`} className="text-[#635BFF] hover:underline">
                      {selectedDemande.email}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-500" />
                  <span>{selectedDemande.localisation}</span>
                </div>
                {selectedDemande.entreprise && (
                  <div className="flex items-center gap-3">
                    <HardHat className="w-5 h-5 text-gray-500" />
                    <span>{selectedDemande.entreprise}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Type-specific details */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Détails de la demande</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  {selectedDemande.type === 'vente' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Type de matériel</dt>
                        <dd className="font-medium">{selectedDemande.type_materiel}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Prix souhaité</dt>
                        <dd className="font-medium text-[#635BFF]">
                          {selectedDemande.prix_souhaite ? `${selectedDemande.prix_souhaite.toLocaleString()} €` : 'Non précisé'}
                        </dd>
                      </div>
                      {selectedDemande.description && (
                        <div className="col-span-2">
                          <dt className="text-gray-500">Description</dt>
                          <dd className="font-medium">{selectedDemande.description}</dd>
                        </div>
                      )}
                    </>
                  )}

                  {selectedDemande.type === 'location' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Type de matériel</dt>
                        <dd className="font-medium">{selectedDemande.type_materiel}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Tarif proposé</dt>
                        <dd className="font-medium">{selectedDemande.tarif_propose || 'Non précisé'}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-gray-500">Disponibilité</dt>
                        <dd className="font-medium">{selectedDemande.periode_disponibilite || 'Non précisée'}</dd>
                      </div>
                    </>
                  )}

                  {selectedDemande.type === 'service' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Type de service</dt>
                        <dd className="font-medium">{selectedDemande.type_service}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Budget</dt>
                        <dd className="font-medium">{selectedDemande.budget || 'Non précisé'}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-gray-500">Besoin</dt>
                        <dd className="font-medium">{selectedDemande.description_besoin}</dd>
                      </div>
                      {selectedDemande.urgence && (
                        <div className="col-span-2">
                          <Badge className="bg-red-500 text-white">URGENT</Badge>
                        </div>
                      )}
                    </>
                  )}

                  {selectedDemande.type === 'etude' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Type de mission</dt>
                        <dd className="font-medium">{selectedDemande.type_mission}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Type de projet</dt>
                        <dd className="font-medium">{selectedDemande.type_projet}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Lieu du projet</dt>
                        <dd className="font-medium">{selectedDemande.lieu_projet}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Délai souhaité</dt>
                        <dd className="font-medium">{selectedDemande.delai_souhaite}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Surface</dt>
                        <dd className="font-medium">{selectedDemande.surface || 'Non précisée'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Budget</dt>
                        <dd className="font-medium">{selectedDemande.budget || 'Non précisé'}</dd>
                      </div>
                    </>
                  )}

                  {selectedDemande.type === 'travaux' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Type de travaux</dt>
                        <dd className="font-medium">{selectedDemande.type_travaux}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Localisation chantier</dt>
                        <dd className="font-medium">{selectedDemande.localisation_chantier}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-gray-500">Volume / Description</dt>
                        <dd className="font-medium">{selectedDemande.volume_description}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Délai souhaité</dt>
                        <dd className="font-medium">{selectedDemande.delai_souhaite}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Budget</dt>
                        <dd className="font-medium">{selectedDemande.budget || 'Non précisé'}</dd>
                      </div>
                    </>
                  )}

                  {selectedDemande.type === 'recrutement_employeur' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Type de poste</dt>
                        <dd className="font-medium">{selectedDemande.type_poste}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Localisation</dt>
                        <dd className="font-medium">{selectedDemande.localisation_poste}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-gray-500">Description du poste</dt>
                        <dd className="font-medium">{selectedDemande.description_poste}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Date de démarrage</dt>
                        <dd className="font-medium">{selectedDemande.date_demarrage}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Type de contrat</dt>
                        <dd className="font-medium">{selectedDemande.type_contrat}</dd>
                      </div>
                    </>
                  )}

                  {selectedDemande.type === 'recrutement_candidat' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Prénom</dt>
                        <dd className="font-medium">{selectedDemande.prenom}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Poste recherché</dt>
                        <dd className="font-medium">{selectedDemande.type_poste_recherche}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Expérience</dt>
                        <dd className="font-medium">{selectedDemande.annees_experience} ans</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Mobilité</dt>
                        <dd className="font-medium">{selectedDemande.mobilite_geographique}</dd>
                      </div>
                    </>
                  )}

                  {selectedDemande.type === 'visite_rachat' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Type de matériel</dt>
                        <dd className="font-medium">{selectedDemande.type_materiel_label || selectedDemande.type_materiel}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Marque / Modèle</dt>
                        <dd className="font-medium">{selectedDemande.marque} {selectedDemande.modele}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Année</dt>
                        <dd className="font-medium">{selectedDemande.annee || 'Non précisée'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Heures de forage</dt>
                        <dd className="font-medium">{selectedDemande.heures || 'Non précisées'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">État</dt>
                        <dd className="font-medium">{selectedDemande.etat_label || selectedDemande.etat || 'Non précisé'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Adresse visite</dt>
                        <dd className="font-medium">{selectedDemande.adresse}, {selectedDemande.code_postal} {selectedDemande.ville}</dd>
                      </div>
                      {selectedDemande.disponibilites && (
                        <div className="col-span-2">
                          <dt className="text-gray-500">Disponibilités</dt>
                          <dd className="font-medium">{selectedDemande.disponibilites}</dd>
                        </div>
                      )}
                      {selectedDemande.commentaires && (
                        <div className="col-span-2">
                          <dt className="text-gray-500">Commentaires</dt>
                          <dd className="font-medium">{selectedDemande.commentaires}</dd>
                        </div>
                      )}
                    </>
                  )}

                  {selectedDemande.type === 'estimation_etude_sol' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Mission géotechnique</dt>
                        <dd className="font-medium">{selectedDemande.mission_label || selectedDemande.mission}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Surface</dt>
                        <dd className="font-medium">{selectedDemande.surface ? `${selectedDemande.surface} m²` : 'Non précisée'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Configuration terrain</dt>
                        <dd className="font-medium">{selectedDemande.complexite_label || 'Non précisée'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Délai</dt>
                        <dd className="font-medium">{selectedDemande.delai_label || 'Non précisé'}</dd>
                      </div>
                      {(selectedDemande.estimation_bas || selectedDemande.estimation_haut) && (
                        <div className="col-span-2">
                          <dt className="text-gray-500">Estimation calculée</dt>
                          <dd className="font-medium text-teal-600 text-lg">
                            {selectedDemande.estimation_bas?.toLocaleString()} € – {selectedDemande.estimation_haut?.toLocaleString()} € HT
                          </dd>
                        </div>
                      )}
                    </>
                  )}

                  {selectedDemande.type === 'estimation_foreuse' && (
                    <>
                      <div>
                        <dt className="text-gray-500">Marque</dt>
                        <dd className="font-medium">{selectedDemande.marque}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Modèle</dt>
                        <dd className="font-medium">{selectedDemande.modele}</dd>
                      </div>
                      {selectedDemande.serie && (
                        <div>
                          <dt className="text-gray-500">N° série</dt>
                          <dd className="font-medium">{selectedDemande.serie}</dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-gray-500">Prix neuf référence</dt>
                        <dd className="font-medium">{selectedDemande.prix_neuf?.toLocaleString()} €</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Année</dt>
                        <dd className="font-medium">{selectedDemande.annee}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Heures de forage</dt>
                        <dd className="font-medium">{selectedDemande.heures?.toLocaleString()} h</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">État</dt>
                        <dd className="font-medium">{selectedDemande.etat_label || selectedDemande.etat}</dd>
                      </div>
                      {selectedDemande.is_residuel ? (
                        <div className="col-span-2">
                          <dt className="text-gray-500">Estimation</dt>
                          <dd className="font-medium text-amber-600">Machine en fin de cycle – visite requise</dd>
                        </div>
                      ) : (selectedDemande.estimation_bas || selectedDemande.estimation_haut) && (
                        <div className="col-span-2">
                          <dt className="text-gray-500">Estimation calculée</dt>
                          <dd className="font-medium text-indigo-600 text-lg">
                            {selectedDemande.estimation_bas?.toLocaleString()} € – {selectedDemande.estimation_haut?.toLocaleString()} €
                          </dd>
                        </div>
                      )}
                    </>
                  )}
                </dl>
              </CardContent>
            </Card>

            {/* Timestamps */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Historique</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Créée le</span>
                    <span className="font-medium">{formatDate(selectedDemande.created_at)}</span>
                  </div>
                  {selectedDemande.contacted_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Premier contact</span>
                      <span className="font-medium">{formatDate(selectedDemande.contacted_at)}</span>
                    </div>
                  )}
                  {selectedDemande.concluded_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Conclu le</span>
                      <span className="font-medium text-green-600">{formatDate(selectedDemande.concluded_at)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Admin notes */}
            {selectedDemande.notes_admin && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Notes internes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedDemande.notes_admin}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-white border-t p-4 space-y-3">
            {/* Convert to Annonce - Only for vente/location */}
            {(selectedDemande.type === 'vente' || selectedDemande.type === 'location') && !selectedDemande.annonce_id && (
              <Button 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => convertToAnnonce(selectedDemande.id)}
                disabled={converting}
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                {converting ? 'Création en cours...' : 'Créer une annonce'}
              </Button>
            )}
            
            {/* Link to existing annonce */}
            {selectedDemande.annonce_id && (
              <Button 
                className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90"
                onClick={() => navigate(`/admin/products?edit=${selectedDemande.annonce_id}`)}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Voir/Modifier l'annonce
              </Button>
            )}
            
            {/* Contact actions */}
            <div className="flex gap-3">
              <Button 
                className="flex-1 bg-[#635BFF] hover:bg-[#635BFF]/90"
                onClick={() => window.open(`tel:${selectedDemande.telephone}`)}
              >
                <Phone className="w-4 h-4 mr-2" />
                Appeler
              </Button>
              {selectedDemande.email && (
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => window.open(`mailto:${selectedDemande.email}`)}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50" data-testid="admin-demandes-dashboard">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[#0A2540]">Pipeline Demandes</h1>
            <p className="text-gray-500">Gérez les leads entrants des 6 piliers</p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-4">
                  <p className="text-blue-100 text-sm">Total</p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-4">
                  <p className="text-green-100 text-sm">Aujourd'hui</p>
                  <p className="text-3xl font-bold">{stats.new_today}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-gray-500 text-sm">Nouveaux</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.by_status?.nouveau || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-gray-500 text-sm">Contactés</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.by_status?.contacte || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-gray-500 text-sm">En cours</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.by_status?.en_cours || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-gray-500 text-sm">Conclus</p>
                  <p className="text-2xl font-bold text-green-600">{stats.by_status?.conclu || 0}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Type Filter */}
            <div className="flex flex-wrap gap-2">
              {DEMANDE_TYPES.map(type => {
                const Icon = type.icon;
                const count = type.id === 'all' ? stats?.total : stats?.by_type?.[type.id];
                return (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedType === type.id
                        ? 'bg-[#635BFF] text-white shadow-lg shadow-[#635BFF]/25'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{type.label}</span>
                    {count !== undefined && (
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        selectedType === type.id ? 'bg-white/20' : 'bg-gray-100'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search and Status Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Rechercher par nom, téléphone, email, localisation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 rounded-lg border bg-white"
            >
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>

          {/* Demandes Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#635BFF]" />
            </div>
          ) : filteredDemandes.length === 0 ? (
            <Card className="p-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune demande trouvée</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredDemandes.map(demande => (
                <DemandeCard key={demande.id} demande={demande} />
              ))}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {showDetailModal && <DetailModal />}
      </div>
    </AdminLayout>
  );
}
